import { useMemo } from 'react';
import {
  useIngestionSummary,
  usePhase0HandoffStatus,
  useProjectKnowledgeGraphStats,
  useProjectWorkflow,
  useUnderstandingStatus,
  type ProjectWorkflowResponse,
  type WorkflowPhaseStatus,
} from '../../../services/api';

/** Cyclic AMS lifecycle — no terminal "complete" state for projects. */
export type PhaseAccess = 'active' | 'available' | 'locked';

export interface ProjectLifecycle {
  hasIngestedFiles: boolean;
  hasKnowledgeGraph: boolean;
  phase0HandoffAccepted: boolean;
  understandingCompleted: boolean;
  phaseAccess: Record<'0' | '1' | '2' | '3', PhaseAccess>;
  continuePath: string;
  continueLabel: string;
  overallProgress?: number;
  currentPhase?: ProjectWorkflowResponse['current_phase'];
  phaseProgress?: Record<'0' | '1' | '2' | '3', number>;
  workflowSource?: 'orchestrator' | 'client';
}

const WORKFLOW_TO_PLATFORM_PHASE: Record<
  ProjectWorkflowResponse['current_phase'],
  '0' | '1' | '2' | '3'
> = {
  discover: '0',
  understand: '1',
  change: '2',
  operate: '3',
};

function mapWorkflowPhaseStatus(status: WorkflowPhaseStatus): PhaseAccess {
  switch (status) {
    case 'locked':
      return 'locked';
    case 'active':
    case 'running':
    case 'partial':
      return 'active';
    default:
      return 'available';
  }
}

function readinessReady(workflow: ProjectWorkflowResponse | undefined, id: string): boolean {
  return workflow?.readiness?.find((item) => item.id === id)?.ready ?? false;
}

function lifecycleFromWorkflow(
  projectId: string,
  workflow: ProjectWorkflowResponse,
): ProjectLifecycle {
  const phaseAccess = {
    '0': 'locked',
    '1': 'locked',
    '2': 'available',
    '3': 'available',
  } as Record<'0' | '1' | '2' | '3', PhaseAccess>;

  const phaseProgress = {
    '0': 0,
    '1': 0,
    '2': 0,
    '3': 0,
  } as Record<'0' | '1' | '2' | '3', number>;

  for (const phase of workflow.phases) {
    const platformId = WORKFLOW_TO_PLATFORM_PHASE[phase.id];
    phaseAccess[platformId] = mapWorkflowPhaseStatus(phase.status);
    phaseProgress[platformId] = phase.progress;
  }

  return {
    hasIngestedFiles: readinessReady(workflow, 'repository_ingestion'),
    hasKnowledgeGraph: readinessReady(workflow, 'knowledge_graph'),
    phase0HandoffAccepted:
      readinessReady(workflow, 'handoff_accepted') || readinessReady(workflow, 'baseline_locked'),
    understandingCompleted: readinessReady(workflow, 'understanding_complete'),
    phaseAccess,
    continuePath: workflow.next_action.path || `/projects/${projectId}/operate/overview`,
    continueLabel: workflow.next_action.label || 'Continue',
    overallProgress: workflow.progress.overall,
    currentPhase: workflow.current_phase,
    phaseProgress,
    workflowSource: 'orchestrator',
  };
}

function lifecycleFromClientSignals(
  projectId: string,
  summary: { file_count?: number } | undefined,
  kgStats: { node_count?: number } | undefined,
  handoff: { status?: string } | undefined,
  understanding: { workers?: Array<{ status: string }> } | undefined,
): ProjectLifecycle {
  const hasIngestedFiles = (summary?.file_count ?? 0) > 0;
  const hasKnowledgeGraph = (kgStats?.node_count ?? 0) > 0;
  const phase0HandoffAccepted = handoff?.status === 'accepted';
  const workers = understanding?.workers ?? [];
  const understandingCompleted =
    workers.length > 0 &&
    workers.every((w) => w.status === 'completed' || w.status === 'failed') &&
    workers.some((w) => w.status === 'completed');

  const phase0: PhaseAccess = hasIngestedFiles ? 'available' : 'active';
  const phase1: PhaseAccess = hasIngestedFiles ? 'available' : 'locked';
  const phase2: PhaseAccess = 'available';
  const phase3: PhaseAccess = 'available';

  let continuePath = `/projects/${projectId}/operate/overview`;
  let continueLabel = 'AMS incidents';
  if (!hasIngestedFiles) {
    continuePath = `/projects/${projectId}/phase-0/repository`;
    continueLabel = 'Upload repository';
  } else if (!hasKnowledgeGraph) {
    continuePath = `/projects/${projectId}/phase-1/graph`;
    continueLabel = 'Build knowledge graph';
  } else if (!phase0HandoffAccepted) {
    continuePath = `/projects/${projectId}/phase-0/review`;
    continueLabel = 'Review baseline';
  } else if (!understandingCompleted) {
    continuePath = `/projects/${projectId}/phase-1/insights`;
    continueLabel = 'View insights';
  }

  return {
    hasIngestedFiles,
    hasKnowledgeGraph,
    phase0HandoffAccepted,
    understandingCompleted,
    phaseAccess: {
      '0': phase0,
      '1': phase1,
      '2': phase2,
      '3': phase3,
    },
    continuePath,
    continueLabel,
    workflowSource: 'client',
  };
}

export function useProjectLifecycle(projectId: string): ProjectLifecycle {
  const { data: workflow } = useProjectWorkflow(projectId);
  const hasWorkflow = Boolean(workflow);
  const { data: summary } = useIngestionSummary(projectId, { poll: !hasWorkflow });
  const { data: kgStats } = useProjectKnowledgeGraphStats(projectId);
  const { data: handoff } = usePhase0HandoffStatus(projectId);
  const understandPhaseActive =
    workflow?.phases.find((p) => p.id === 'understand')?.status === 'running' ||
    workflow?.phases.find((p) => p.id === 'understand')?.status === 'active';
  const { data: understanding } = useUnderstandingStatus(projectId, {
    poll: !hasWorkflow || Boolean(understandPhaseActive),
  });

  return useMemo(() => {
    if (workflow) {
      return lifecycleFromWorkflow(projectId, workflow);
    }
    return lifecycleFromClientSignals(projectId, summary, kgStats, handoff, understanding);
  }, [projectId, workflow, summary, kgStats, handoff, understanding]);
}
